import React, { useState } from 'react';

// The core of the app: every project task and its activities, editable before anything
// is written to Acumatica. Edits mutate the parsed-quote object the parent holds, so
// the plan preview and the create call always reflect what's on screen.

const RULE_STYLES = {
  FIXED: 'bg-blue-50 text-blue-700 ring-blue-200',
  TMR: 'bg-amber-50 text-amber-700 ring-amber-200',
};

// Progress Billing Basis is derived from the billing rule, never chosen — showing it
// read-only makes the consequence of flipping FIXED/TMR visible.
const PROGRESS_BILLING_BASIS = { FIXED: 'Amount', TMR: 'Quantity' };

function Pill({ children, className = '' }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${className}`}>
      {children}
    </span>
  );
}

function IconButton({ title, onClick, children, danger }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      className={`rounded-lg px-2 py-1 text-sm transition-colors ${
        danger ? 'text-gray-400 hover:bg-red-50 hover:text-red-600' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700'
      }`}
    >
      {children}
    </button>
  );
}

function Activity({ activity, onChange, onRemove, onMove, isFirst, isLast }) {
  return (
    <div className="group flex items-center gap-2 border-t border-gray-100 py-1.5 pl-6 pr-2 first:border-t-0">
      <input
        value={activity.summary}
        onChange={(e) => onChange({ ...activity, summary: e.target.value })}
        className="min-w-0 flex-1 rounded-lg border border-transparent bg-transparent px-2 py-1 text-sm text-gray-700
                   hover:border-gray-200 focus:border-blue-400 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
      />
      <div className="flex items-center gap-1">
        <input
          type="number"
          min="0"
          step="0.5"
          value={activity.hours}
          onChange={(e) => onChange({ ...activity, hours: parseFloat(e.target.value) || 0 })}
          title="Written to the activity's Budget Time"
          className="w-16 rounded-lg border border-gray-200 px-2 py-1 text-right text-sm tabular-nums
                     focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
        />
        <span className="w-3 text-xs text-gray-400">h</span>
      </div>
      {activity.hours === 0 && (
        <Pill className="bg-gray-100 text-gray-500 ring-gray-200">won't be created</Pill>
      )}
      <div className="flex opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
        <IconButton title="Move up" onClick={() => onMove(-1)}>{isFirst ? ' ' : '↑'}</IconButton>
        <IconButton title="Move down" onClick={() => onMove(1)}>{isLast ? ' ' : '↓'}</IconButton>
        <IconButton title="Remove activity" onClick={onRemove} danger>✕</IconButton>
      </div>
    </div>
  );
}

function Task({ task, onChange, onRemove }) {
  const [open, setOpen] = useState(true);
  const hours = task.activities.reduce((sum, a) => sum + (a.hours || 0), 0);

  const setActivities = (activities) => onChange({ ...task, activities });
  const updateActivity = (i, next) => setActivities(task.activities.map((a, j) => (j === i ? next : a)));
  const removeActivity = (i) => setActivities(task.activities.filter((_, j) => j !== i));
  const moveActivity = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= task.activities.length) return;
    const next = [...task.activities];
    [next[i], next[j]] = [next[j], next[i]];
    setActivities(next);
  };
  const addActivity = () => setActivities([...task.activities, { summary: 'New activity', hours: 0 }]);

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center gap-3 px-3 py-2">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          className="text-gray-400 transition-transform hover:text-gray-600"
          style={{ transform: open ? 'rotate(90deg)' : 'none' }}
        >
          ▸
        </button>

        <code className="shrink-0 rounded-md bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-600">
          {task.taskId || '—'}
        </code>

        <input
          value={task.description}
          onChange={(e) => onChange({ ...task, description: e.target.value })}
          className="min-w-0 flex-1 rounded-lg border border-transparent px-2 py-1 text-sm font-medium text-gray-900
                     hover:border-gray-200 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
        />

        <select
          value={task.billingRule || ''}
          onChange={(e) => onChange({ ...task, billingRule: e.target.value })}
          className={`rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
            RULE_STYLES[task.billingRule] || 'bg-red-50 text-red-700 ring-red-200'
          }`}
        >
          <option value="FIXED">FIXED</option>
          <option value="TMR">TMR</option>
        </select>

        <span className="w-16 shrink-0 text-xs text-gray-400" title="Progress billing basis (follows the billing rule)">
          {PROGRESS_BILLING_BASIS[task.billingRule] || '—'}
        </span>

        <span className="w-14 shrink-0 text-right text-sm tabular-nums text-gray-500">{hours}h</span>
        <span className="w-20 shrink-0 text-right text-xs text-gray-400">
          {task.activities.length} {task.activities.length === 1 ? 'activity' : 'activities'}
        </span>
        <IconButton title="Remove task" onClick={onRemove} danger>✕</IconButton>
      </div>

      {open && (
        <div className="border-t border-gray-100 bg-gray-50/60 px-3 py-1">
          {task.activities.map((activity, i) => (
            <Activity
              key={i}
              activity={activity}
              isFirst={i === 0}
              isLast={i === task.activities.length - 1}
              onChange={(next) => updateActivity(i, next)}
              onRemove={() => removeActivity(i)}
              onMove={(delta) => moveActivity(i, delta)}
            />
          ))}
          {!task.activities.length && (
            <p className="px-6 py-2 text-sm text-gray-400">
              No activities — this task will be created empty.
            </p>
          )}
          <button
            type="button"
            onClick={addActivity}
            className="my-1 ml-6 rounded-lg px-2 py-1 text-sm font-medium text-blue-600 hover:bg-blue-50"
          >
            + Add activity
          </button>
        </div>
      )}
    </div>
  );
}

export default function TaskEditor({ modules, onChange }) {
  const updateModule = (mi, next) => onChange(modules.map((m, i) => (i === mi ? next : m)));

  return (
    <div className="space-y-6">
      {modules.map((module, mi) => {
        const hours = module.tasks.reduce(
          (sum, t) => sum + t.activities.reduce((s, a) => s + (a.hours || 0), 0), 0);

        return (
          <section key={module.name}>
            <header className="mb-2 flex items-center gap-3">
              <h2 className="text-base font-semibold text-gray-900">{module.name}</h2>
              <code className="rounded-md bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-600">
                {module.prefix || '??'}
              </code>
              {!module.prefixConfirmed && (
                <Pill className="bg-amber-50 text-amber-700 ring-amber-200">prefix unverified</Pill>
              )}
              <span className="ml-auto text-sm tabular-nums text-gray-500">{hours}h</span>
            </header>

            <div className="space-y-2">
              {module.tasks.map((task, ti) => (
                <Task
                  key={task.taskId || ti}
                  task={task}
                  onChange={(next) =>
                    updateModule(mi, { ...module, tasks: module.tasks.map((t, j) => (j === ti ? next : t)) })}
                  onRemove={() =>
                    updateModule(mi, { ...module, tasks: module.tasks.filter((_, j) => j !== ti) })}
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
